import React from 'react'
import PropTypes from 'prop-types'
import { Controller, useFormContext } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydSelect from '../../../../commonDesign/SydSelect'
import Switch from '../../../../molecules/Switch'
import StyleVariantPicker from '../../../../pages/denali/Analysis/QueryBlueprints/components/presentationManagement/typeEditors/StyleVariantPicker'
import { PAGE_SIZE_OPTIONS } from './constants'
import QuickDateChecklist, { quickDateOptions } from './QuickDateChecklist'
import ColumnsSection, { LocalStorageKeyField } from './ColumnsSection'
import MetricGroupsEditor from './MetricGroupsEditor'

const sectionHeading = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  borderBottom: '1px solid var(--color-neutral-200)',
  paddingBottom: '4px'
}

const pageSizeSelectOptions = PAGE_SIZE_OPTIONS.map(s => ({ value: s, label: String(s) }))

const sortDirectionOptions = [
  { value: 'asc', label: 'Ascending' },
  { value: 'desc', label: 'Descending' }
]

const sortFieldOptions = [
  { value: 'transdate', label: 'Date' },
  { value: 'transamount', label: 'Amount' },
  { value: 'nounits', label: 'Units' },
  { value: 'settledate', label: 'Settle Date' },
  { value: 'ticker', label: 'Ticker' },
  { value: 'productname', label: 'Product' }
]

const scopeOptions = [
  { value: 'client', label: 'Client — data for the current client' }
]

const metricLayoutOptions = [
  { value: 'stretch', label: 'Stretch — fill horizontal space' },
  { value: 'fixed', label: 'Fixed — fixed-width, left aligned' },
  { value: 'centered', label: 'Centered — fixed-width, centered' }
]

const detailViewOptions = [
  { value: 'disabled', label: 'Disabled — no detail view' },
  { value: 'modal', label: 'Modal — popup dialog' },
  { value: 'drawer', label: 'Drawer — slide-out panel' }
]

function SwitchField ({ name, control, label }) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
          <span style={{ fontSize: 'var(--font-size-sm)' }}>{label}</span>
          <Switch
            checked={!!field.value}
            onChange={(e) => field.onChange(e.target.checked)}
          />
        </div>
      )}
    />
  )
}

SwitchField.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired
}

function SyncedTransactionsConfigEditor ({ fieldPrefix }) {
  const { control, setValue } = useFormContext()
  const configPrefix = `${fieldPrefix}.config`

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <div style={sectionHeading}>Scope</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.scopeType`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Scope' helperText='Data scope for the transaction query'>
              <SydSelect
                options={scopeOptions}
                value={field.value || 'client'}
                onChange={(e) => field.onChange(e.target.value)}
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Component Rules</div>
      </Grid>
      <Grid item xs={6}>
        <SwitchField name={`${configPrefix}.rules.excludeCustodialCashBuySell`} control={control} label='Exclude Custodial Cash from Buys & Sells' />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Detail View</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.detailView`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Detail View Presentation' helperText='How the transaction detail is displayed when a row is clicked'>
              <SydSelect
                options={detailViewOptions}
                value={field.value || 'modal'}
                onChange={(e) => field.onChange(e.target.value)}
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Date Range</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.defaultQuickDate`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Default Quick Date' helperText='Initial date range preset'>
              <SydSelect
                options={quickDateOptions}
                value={field.value || 'YTD'}
                onChange={(e) => field.onChange(e.target.value)}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.availableQuickDates`}
          control={control}
          render={({ field }) => (
            <QuickDateChecklist value={field.value} onChange={field.onChange} />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Available Filters</div>
      </Grid>
      <Grid item xs={6}>
        <SwitchField name={`${configPrefix}.availableFilters.transactionTypes`} control={control} label='Transaction Types (hierarchy)' />
        <SwitchField name={`${configPrefix}.availableFilters.iscustodialcash`} control={control} label='Custodial Cash' />
      </Grid>
      <Grid item xs={6}>
        <SwitchField name={`${configPrefix}.availableFilters.transamount`} control={control} label='Amount Range' />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Table Configuration</div>
      </Grid>
      <Grid item xs={12}>
        <ColumnsSection configPrefix={configPrefix} control={control} setValue={setValue} />
      </Grid>
      <Grid item xs={6}>
        <LocalStorageKeyField configPrefix={configPrefix} control={control} />
      </Grid>
      <Grid item xs={3}>
        <SwitchField name={`${configPrefix}.showExport`} control={control} label='Show Export' />
      </Grid>
      <Grid item xs={3}>
        <SwitchField name={`${configPrefix}.showColumnConfig`} control={control} label='Show Column Config' />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Table Style</div>
      </Grid>
      <Controller
        name={`${configPrefix}.tableStyleVariant`}
        control={control}
        render={({ field: variantField }) => (
          <Controller
            name={`${configPrefix}.tableStyleVariantParams`}
            control={control}
            render={({ field: paramsField }) => (
              <StyleVariantPicker
                presentationType='Hierarchical Grid'
                value={variantField.value || 'default'}
                onChange={(v) => {
                  variantField.onChange(v)
                  paramsField.onChange({})
                }}
                variantParams={paramsField.value || {}}
                onParamsChange={(p) => paramsField.onChange(p)}
              />
            )}
          />
        )}
      />

      <Grid item xs={12}>
        <div style={sectionHeading}>Sorting &amp; Pagination</div>
      </Grid>
      <Grid item xs={4}>
        <Controller
          name={`${configPrefix}.defaultSort.field`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Default Sort Field'>
              <SydSelect
                options={sortFieldOptions}
                value={field.value || 'transdate'}
                onChange={(e) => field.onChange(e.target.value)}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          name={`${configPrefix}.defaultSort.dir`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Sort Direction'>
              <SydSelect
                options={sortDirectionOptions}
                value={field.value || 'desc'}
                onChange={(e) => field.onChange(e.target.value)}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          name={`${configPrefix}.defaultPageSize`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Default Page Size'>
              <SydSelect
                options={pageSizeSelectOptions}
                value={field.value || 50}
                onChange={(e) => field.onChange(Number(e.target.value))}
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Metric Cards</div>
      </Grid>
      <Grid item xs={6}>
        <SwitchField name={`${configPrefix}.showMetrics`} control={control} label='Show Metric Cards' />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.metricLayout`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Card Layout'>
              <SydSelect
                options={metricLayoutOptions}
                value={field.value || 'stretch'}
                onChange={(e) => field.onChange(e.target.value)}
                size='sm'
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Controller
        name={`${configPrefix}.showMetrics`}
        control={control}
        render={({ field: { value: showMetrics } }) => showMetrics !== false ? (
          <Grid item xs={12}>
            <MetricGroupsEditor configPrefix={configPrefix} control={control} />
          </Grid>
        ) : null}
      />
    </Grid>
  )
}

SyncedTransactionsConfigEditor.propTypes = {
  fieldPrefix: PropTypes.string.isRequired
}

export default SyncedTransactionsConfigEditor
